import { FlexBox, Icon, IconTypes } from '@fable/components'
import { css, cx, useTheme } from '@fable/theme'
import { CompProps } from '@fable/types'
import { useRef } from 'react'
import { useClickOutside } from '@fable/hooks'
import { AnimatePresence, motion } from 'framer-motion'
import { CompChildren } from 'types'

export interface OverflowMenuProps {
  dataTestId?: string
  icon?: IconTypes
  iconColor?: string
  iconBgColor?: string
  size?: 'S' | 'M'
  trigger?: CompChildren
  align?: 'left' | 'center' | 'right'
  disabled?: boolean
  containerStyles?: string
  wrapperStyles?: string
  open: boolean
  setOpen: (open: boolean) => void
  free?: boolean
  hasCloseButton?: boolean
  onTriggerClick?: () => void
}

export const OverflowMenu = ({
  dataTestId = '',
  children,
  icon = 'settings',
  iconColor,
  iconBgColor,
  size = 'M',
  trigger,
  align = 'right',
  disabled = false,
  containerStyles = '',
  wrapperStyles = '',
  open,
  setOpen,
  free = false,
  hasCloseButton = false,
  onTriggerClick,
}: CompProps<OverflowMenuProps>) => {
  const ref = useRef(null)
  const { colors } = useTheme()
  const triggerIconColor = iconColor || colors.uiWhite
  const triggerIconBgColor = iconBgColor || colors.uiBlack20

  useClickOutside(ref, () => open && setOpen(false))

  const getSize = () => {
    switch (size) {
      case 'S':
        return { container: 32, icon: 16 }
      case 'M':
        return { container: 40, icon: 20 }
    }
  }

  const sizes = getSize()

  const handleOpen = () => {
    if (!open && !disabled) {
      setOpen(true)
      onTriggerClick?.()
    }
  }

  const getTrigger = () => {
    if (trigger) return <div onClick={handleOpen}>{trigger}</div>

    return (
      <button
        data-testid={dataTestId}
        className={css`
          width: ${sizes.container}px;
          height: ${sizes.container}px;
          background-color: ${open ? colors.uiGraphite : triggerIconBgColor};
          border-radius: 50%;
          border: none;
          display: flex;
          justify-content: center;
          align-items: center;
          cursor: pointer;
          flex-shrink: 0;
        `}
        onClick={handleOpen}
        aria-label="open stats menu"
      >
        <Icon
          className={css`
            width: ${sizes.icon}px;
          `}
          type={icon}
          fill={triggerIconColor}
        />
      </button>
    )
  }

  const getAlign = () => {
    if (free) return ''

    switch (align) {
      case 'left':
        return css`
          left: 0;
        `
      case 'right':
        return css`
          right: 0;
        `
      case 'center':
        return css`
          left: 50%;
        `
    }
  }

  const motionX = align === 'center' ? '-50%' : 0

  return (
    <div
      className={
        free
          ? ''
          : css`
              position: relative;
            `
      }
    >
      {getTrigger()}
      <AnimatePresence>
        {open ? (
          <motion.div
            ref={ref}
            key="overflow-menu"
            layout
            initial={{ opacity: 0, y: 10, x: motionX }}
            animate={{ opacity: 1, y: 0, x: motionX }}
            exit={{ opacity: 0, y: 10, x: motionX }}
            transition={{ duration: 0.2 }}
            className={cx(
              css`
                position: absolute;
                top: 0;
                z-index: 50;
              `,
              getAlign(),
              wrapperStyles
            )}
          >
            <div
              className={cx(
                css`
                  padding: 20px 0;
                  background-color: ${colors.uiGraphite};
                  border-radius: 8px;
                  width: 250px;
                  overflow-y: auto;
                  box-shadow: 0px 16px 40px 0px rgba(0, 0, 0, 0.6);
                `,
                containerStyles
              )}
              onClick={(e) => e.stopPropagation()}
            >
              {hasCloseButton ? (
                <FlexBox
                  className={css`
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    overflow: hidden;
                    background-color: ${colors.uiBlack40};
                    position: absolute;
                    top: 12px;
                    right: 12px;
                  `}
                  onClick={() => setOpen(false)}
                  centerAll
                >
                  <Icon
                    className={css`
                      width: 16px;
                    `}
                    type="close"
                    fill={colors.uiWhite}
                  />
                </FlexBox>
              ) : null}
              {children}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  )
}
